import {
  AccountAllocationMode,
  AccountInputs,
  FinancialPlanAccounts,
  FinancialPlanData,
} from '../types/financialPlan';
import {
  computeHealthMonthlyReserve,
  computeK12Needs,
  computeLifestyleMonthly,
  debtLedgerAtAge,
  grossIncomeAtAge,
  grossIncomeTodayAtAge,
  k12SpendingAtAge,
  livingMonthlyAtPlanAge,
  yearsToRetirement,
} from './lifetimeGoalsCalc';

import {
  sepIraIncomePctFromMonthly,
  sepIraMonthlyFromAccount,
  sepIraMonthlyFromPct,
} from './lgcSepIra';
import { applyRothIraCapToMonthlyContributions } from './lgcRothIra';

export const ACCOUNT_ALLOCATION_KEYS: (keyof FinancialPlanAccounts)[] = [
  'k401',
  'hsa',
  'rothIra',
  'taxable',
];

export type PrimaryCommitmentLine = {
  id: string;
  label: string;
  monthlyAmount: number;
};

export type InvestablePoolSnapshot = {
  age: number;
  phaseId?: string;
  phaseLabel?: string;
  ageRangeLabel?: string;
  grossIncomeTodayAnnual: number;
  takeHomeMonthly: number;
  primaryCommitments: PrimaryCommitmentLine[];
  primaryCommitmentsMonthly: number;
  /** SEP IRA employer contribution (income-based, not from this pool). */
  sepIraMonthly: number;
  /** Take-home remaining after primary commitments and SEP — basis for HSA/Roth/taxable %. */
  availableToInvestMonthly: number;
  allocatedMonthly: number;
  unallocatedMonthly: number;
  isCurrentPhase: boolean;
};

const POOL_ALLOCATION_KEYS: (keyof FinancialPlanAccounts)[] = ['hsa', 'rothIra', 'taxable'];

function clampNonNeg(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function totalAccountContributions(accounts: FinancialPlanAccounts): number {
  return ACCOUNT_ALLOCATION_KEYS.reduce((s, k) => s + clampNonNeg(accounts[k].monthlyContribution), 0);
}

export function totalAccountContributionPct(accounts: FinancialPlanAccounts): number {
  return POOL_ALLOCATION_KEYS.reduce((s, k) => s + clampNonNeg(accounts[k].contributionPct ?? 0), 0);
}

function sepIraMonthlyForPlanAtAge(plan: FinancialPlanData, age: number): number {
  const grossToday = grossIncomeTodayAtAge(plan.profile, plan.income, age);
  const mode = getAccountAllocationMode(plan);
  return sepIraMonthlyFromAccount(grossToday, plan.accounts.k401, mode);
}

function poolAllocationMonthlyAtAge(
  plan: FinancialPlanData,
  age: number,
  availableMonthly: number,
): number {
  const mode = getAccountAllocationMode(plan);
  if (mode === 'percent') {
    return POOL_ALLOCATION_KEYS.reduce(
      (s, k) => s + monthlyContributionFromPct(availableMonthly, plan.accounts[k].contributionPct ?? 0),
      0,
    );
  }
  return POOL_ALLOCATION_KEYS.reduce((s, k) => s + clampNonNeg(plan.accounts[k].monthlyContribution), 0);
}

/** Monthly costs that are funded before tax-advantaged account contributions. */
export function computePrimaryCommitmentsMonthlyAtAge(
  plan: FinancialPlanData,
  age: number,
): { lines: PrimaryCommitmentLine[]; total: number } {
  const { profile, children, health, lifestyle, debts } = plan;
  const yearsToRetire = yearsToRetirement(profile.currentAge, profile.retirementAge);

  const living = livingMonthlyAtPlanAge(plan, age);
  const debt = debtLedgerAtAge(debts, profile, age).monthlyService;
  const k12Cash = k12SpendingAtAge(children, profile, age) / 12;
  const college529 = children.reduce((s, c) => s + clampNonNeg(c.monthlyContribution529), 0);
  const healthMo = computeHealthMonthlyReserve(health, yearsToRetire);
  const lifestyleMo = computeLifestyleMonthly(lifestyle);
  const k12Savings = computeK12Needs(children, profile, profile.portfolioReturnPct).reduce(
    (s, k) => s + k.monthlySavingsNeeded,
    0,
  );

  const lines: PrimaryCommitmentLine[] = [];
  if (living > 0) lines.push({ id: 'living', label: 'Living & tithing', monthlyAmount: living });
  if (debt > 0) lines.push({ id: 'debt', label: 'Debt service', monthlyAmount: debt });
  if (k12Cash > 0) lines.push({ id: 'k12cash', label: 'K-12 tuition (cash)', monthlyAmount: k12Cash });
  if (college529 > 0) lines.push({ id: '529', label: '529 contributions', monthlyAmount: college529 });
  if (k12Savings > 0) lines.push({ id: 'k12save', label: 'K-12 savings', monthlyAmount: k12Savings });
  if (healthMo > 0) lines.push({ id: 'health', label: 'Health reserve', monthlyAmount: healthMo });
  if (lifestyleMo > 0) lines.push({ id: 'lifestyle', label: 'Lifestyle goals', monthlyAmount: lifestyleMo });

  return { lines, total: lines.reduce((s, line) => s + line.monthlyAmount, 0) };
}

export function representativeAgeForPhase(
  plan: FinancialPlanData,
  startAge: number,
  endAge: number,
): number {
  const { currentAge, retirementAge } = plan.profile;
  if (currentAge >= startAge && currentAge <= endAge) return currentAge;
  return Math.min(Math.max(startAge, currentAge), Math.min(endAge, retirementAge - 1));
}

export function computeInvestablePoolAtAge(
  plan: FinancialPlanData,
  age: number,
  options?: {
    phaseId?: string;
    phaseLabel?: string;
    ageRangeLabel?: string;
    isCurrentPhase?: boolean;
  },
): InvestablePoolSnapshot {
  const { profile, income, accounts } = plan;
  const taxRate = clampNonNeg(profile.effectiveTaxRatePct) / 100;
  const gross = grossIncomeAtAge(profile, income, age);
  const grossToday = grossIncomeTodayAtAge(profile, income, age);
  const takeHomeMonthly = (gross / 12) * (1 - taxRate);
  const { lines, total: primaryCommitmentsMonthly } = computePrimaryCommitmentsMonthlyAtAge(plan, age);
  const sepIraMonthly = sepIraMonthlyForPlanAtAge(plan, age);
  const availableToInvestMonthly = Math.max(
    0,
    takeHomeMonthly - primaryCommitmentsMonthly - sepIraMonthly,
  );
  const poolAllocatedMonthly = poolAllocationMonthlyAtAge(plan, age, availableToInvestMonthly);
  const allocatedMonthly = sepIraMonthly + poolAllocatedMonthly;

  return {
    age,
    phaseId: options?.phaseId,
    phaseLabel: options?.phaseLabel,
    ageRangeLabel: options?.ageRangeLabel,
    grossIncomeTodayAnnual: grossToday,
    takeHomeMonthly,
    primaryCommitments: lines,
    primaryCommitmentsMonthly,
    sepIraMonthly,
    availableToInvestMonthly,
    allocatedMonthly,
    unallocatedMonthly: availableToInvestMonthly - poolAllocatedMonthly,
    isCurrentPhase: options?.isCurrentPhase ?? age === profile.currentAge,
  };
}

export function computeInvestablePoolByPhase(plan: FinancialPlanData): InvestablePoolSnapshot[] {
  const { profile, income } = plan;
  if (income.phases.length === 0) {
    return [
      computeInvestablePoolAtAge(plan, profile.currentAge, {
        phaseLabel: 'Current income',
        ageRangeLabel: `Age ${profile.currentAge}`,
        isCurrentPhase: true,
      }),
    ];
  }

  return income.phases.map((phase) => {
    const age = representativeAgeForPhase(plan, phase.startAge, phase.endAge);
    const isCurrentPhase = profile.currentAge >= phase.startAge && profile.currentAge <= phase.endAge;
    return computeInvestablePoolAtAge(plan, age, {
      phaseId: phase.id,
      phaseLabel: phase.label ?? `Ages ${phase.startAge}–${phase.endAge}`,
      ageRangeLabel: `Ages ${phase.startAge}–${phase.endAge}`,
      isCurrentPhase,
    });
  });
}

export function monthlyContributionFromPct(
  availableMonthly: number,
  contributionPct: number,
): number {
  return availableMonthly * (clampNonNeg(contributionPct) / 100);
}

export function contributionPctFromMonthly(
  availableMonthly: number,
  monthlyContribution: number,
): number {
  if (availableMonthly <= 0) return 0;
  return (clampNonNeg(monthlyContribution) / availableMonthly) * 100;
}

export function applyAccountPatchWithAllocation(
  plan: FinancialPlanData,
  accountKey: keyof FinancialPlanAccounts,
  patch: Partial<AccountInputs>,
  mode: AccountAllocationMode,
  age = plan.profile.currentAge,
): FinancialPlanAccounts {
  const pool = computeInvestablePoolAtAge(plan, age);
  const grossToday = pool.grossIncomeTodayAnnual;
  const availableMonthly = pool.availableToInvestMonthly;
  const current = plan.accounts[accountKey];
  const nextAccount: AccountInputs = { ...current, ...patch };

  if (accountKey === 'k401') {
    if (mode === 'percent') {
      if (patch.contributionPct != null) {
        nextAccount.contributionPct = clampNonNeg(patch.contributionPct);
        nextAccount.monthlyContribution = sepIraMonthlyFromPct(grossToday, nextAccount.contributionPct);
      } else if (patch.monthlyContribution != null) {
        nextAccount.monthlyContribution = sepIraMonthlyFromAccount(grossToday, {
          ...nextAccount,
          monthlyContribution: clampNonNeg(patch.monthlyContribution),
        }, 'dollar');
        nextAccount.contributionPct = sepIraIncomePctFromMonthly(grossToday, nextAccount.monthlyContribution);
      }
    } else if (patch.monthlyContribution != null) {
      nextAccount.monthlyContribution = sepIraMonthlyFromAccount(grossToday, {
        ...nextAccount,
        monthlyContribution: clampNonNeg(patch.monthlyContribution),
      }, 'dollar');
      nextAccount.contributionPct = sepIraIncomePctFromMonthly(grossToday, nextAccount.monthlyContribution);
    }
    return { ...plan.accounts, k401: nextAccount };
  }

  if (mode === 'percent') {
    if (patch.contributionPct != null) {
      nextAccount.contributionPct = clampNonNeg(patch.contributionPct);
      nextAccount.monthlyContribution = monthlyContributionFromPct(
        availableMonthly,
        nextAccount.contributionPct,
      );
    } else if (patch.monthlyContribution != null) {
      nextAccount.monthlyContribution = clampNonNeg(patch.monthlyContribution);
      nextAccount.contributionPct = contributionPctFromMonthly(
        availableMonthly,
        nextAccount.monthlyContribution,
      );
    }
  } else if (patch.monthlyContribution != null) {
    nextAccount.monthlyContribution = clampNonNeg(patch.monthlyContribution);
    nextAccount.contributionPct = contributionPctFromMonthly(
      availableMonthly,
      nextAccount.monthlyContribution,
    );
  }

  return { ...plan.accounts, [accountKey]: nextAccount };
}

export function initializeAccountContributionPercents(
  plan: FinancialPlanData,
): FinancialPlanAccounts {
  const { accounts } = plan;
  const age = plan.profile.currentAge;
  const pool = computeInvestablePoolAtAge(plan, age);
  const grossToday = pool.grossIncomeTodayAnnual;
  const availableMonthly = pool.availableToInvestMonthly;
  const total = totalAccountContributions(accounts);
  const next = { ...accounts } as FinancialPlanAccounts;

  const k401 = { ...accounts.k401 };
  const k401Pct = k401.contributionPct;
  if (k401.employerMatchPct != null && k401.employerMatchPct > 0 && (k401Pct == null || k401Pct === 0)) {
    k401.contributionPct = clampNonNeg(k401.employerMatchPct);
  } else if (k401Pct == null && k401.monthlyContribution > 0 && grossToday > 0) {
    k401.contributionPct = sepIraIncomePctFromMonthly(grossToday, k401.monthlyContribution);
  } else if (k401Pct == null) {
    k401.contributionPct = 0;
  } else {
    k401.contributionPct = clampNonNeg(k401Pct);
  }
  k401.monthlyContribution = sepIraMonthlyFromPct(grossToday, k401.contributionPct ?? 0);
  next.k401 = k401;

  POOL_ALLOCATION_KEYS.forEach((k) => {
    const acct = { ...accounts[k] };
    const pct = acct.contributionPct;

    if (pct == null && acct.monthlyContribution > 0 && availableMonthly > 0) {
      acct.contributionPct = contributionPctFromMonthly(availableMonthly, acct.monthlyContribution);
    } else if (pct == null && acct.monthlyContribution <= 0) {
      acct.contributionPct = 100 / POOL_ALLOCATION_KEYS.length;
    } else if (pct != null) {
      acct.contributionPct = clampNonNeg(pct);
    }

    if (total <= 0 && availableMonthly > 0 && pct == null) {
      acct.monthlyContribution = monthlyContributionFromPct(availableMonthly, acct.contributionPct ?? 0);
    }
    next[k] = acct;
  });
  return next;
}

export function syncPercentModeContributions(
  accounts: FinancialPlanAccounts,
  plan: FinancialPlanData,
  age = plan.profile.currentAge,
): FinancialPlanAccounts {
  const pool = computeInvestablePoolAtAge({ ...plan, accounts }, age);
  const grossToday = pool.grossIncomeTodayAnnual;
  const availableMonthly = pool.availableToInvestMonthly;
  const next = { ...accounts } as FinancialPlanAccounts;

  next.k401 = {
    ...accounts.k401,
    monthlyContribution: sepIraMonthlyFromPct(grossToday, accounts.k401.contributionPct ?? 0),
  };

  POOL_ALLOCATION_KEYS.forEach((k) => {
    next[k] = {
      ...accounts[k],
      monthlyContribution: monthlyContributionFromPct(availableMonthly, accounts[k].contributionPct ?? 0),
    };
  });
  return next;
}

export function getAccountAllocationMode(plan: FinancialPlanData): AccountAllocationMode {
  return plan.accountSettings?.allocationMode === 'dollar' ? 'dollar' : 'percent';
}

export {
  accountInvestmentWeights,
  addLumpSumToAccountsByWeight,
  distributeExtraToAccounts,
} from './lgcAccountRouting';

export function resolvePlanWithPercentAllocations(plan: FinancialPlanData): FinancialPlanData {
  return resolvePlanAccountContributions(plan);
}

/** Sync account dollar amounts from allocation inputs (SEP from income; others from pool). */
export function resolvePlanAccountContributions(plan: FinancialPlanData): FinancialPlanData {
  const mode = getAccountAllocationMode(plan);
  if (mode === 'percent') {
    return {
      ...plan,
      accounts: syncPercentModeContributions(plan.accounts, plan),
    };
  }
  const grossToday = grossIncomeTodayAtAge(plan.profile, plan.income, plan.profile.currentAge);
  const k401Monthly = sepIraMonthlyFromAccount(grossToday, plan.accounts.k401, 'dollar');
  return {
    ...plan,
    accounts: {
      ...plan.accounts,
      k401: {
        ...plan.accounts.k401,
        monthlyContribution: k401Monthly,
        contributionPct: sepIraIncomePctFromMonthly(grossToday, k401Monthly),
      },
    },
  };
}

/** Monthly contributions for each account at a plan age (SEP from income; others from pool). */
export function accountMonthlyContributionsAtAge(
  plan: FinancialPlanData,
  age: number,
): Record<keyof FinancialPlanAccounts, number> {
  const pool = computeInvestablePoolAtAge(plan, age);
  const grossToday = pool.grossIncomeTodayAnnual;
  const mode = getAccountAllocationMode(plan);
  const sepMonthly = sepIraMonthlyFromAccount(grossToday, plan.accounts.k401, mode);
  const available = pool.availableToInvestMonthly;

  const result = {} as Record<keyof FinancialPlanAccounts, number>;
  result.k401 = sepMonthly;
  POOL_ALLOCATION_KEYS.forEach((k) => {
    if (mode === 'percent') {
      result[k] = monthlyContributionFromPct(available, plan.accounts[k].contributionPct ?? 0);
    } else {
      result[k] = clampNonNeg(plan.accounts[k].monthlyContribution);
    }
  });
  return applyRothIraCapToMonthlyContributions(result, age);
}
